import { defineField, defineType } from 'sanity'
import ScoringInput from '../../components/ScoringInput'
import WheelSizeSelector from '../../components/WheelSizeSelector'
import NumericReferenceInput from '../../components/NumericReferenceInput'
import AIContentWithOverrideInput from '../../components/AIContentWithOverrideInput'
import ListingAIRegenerateButton from '../../components/ListingAIRegenerateButton'
import PerformanceScoreDisplay from '../../components/PerformanceScoreDisplay'

export default defineType({
  name: 'fitmentListing',
  title: 'Fitment Listing',
  type: 'object',
  groups: [
    {
      name: 'specifications',
      title: 'Specifications',
      default: true,
    },
    {
      name: 'scoring',
      title: 'Scoring',
      default: false,
    },
    {
      name: 'aiContent',
      title: 'AI Content',
      default: false,
    },
    {
      name: 'performanceScore',
      title: 'Performance Score',
      default: false,
    },
  ],
  fields: [
    // Wheel Specifications Group
    defineField({
      name: 'wheelSize',
      title: 'Wheel Size',
      type: 'object',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'front',
          title: 'Front',
          type: 'object',
          options: {
            collapsible: false,
          },
          fields: [
            {
              name: 'diameter',
              title: 'Diameter',
              type: 'object',
              options: {
                collapsible: false,
              },
              fields: [
                {
                  name: 'wheelDiameter',
                  title: 'Wheel Diameter',
                  description: 'The diameter of the wheel in inches',
                  type: 'number',
                },
                {
                  name: 'name',
                  title: 'Name (legacy)',
                  description: 'This field will be removed once migration is done',
                  readOnly: true,
                  type: 'string',
                },
              ],
            },
            {
              name: 'width',
              title: 'Width',
              type: 'reference',
              to: [{ type: 'width' }],
              validation: (Rule) => Rule.required().error('Width is required'),
            },
            {
              name: 'offset',
              title: 'Offset',
              type: 'object',
              options: {
                collapsible: false,
              },
              fields: [
                {
                  name: 'name',
                  title: 'Name',
                  type: 'string',
                },
              ],
            },
            {
              name: 'alternativeOffset',
              title: 'Alternative Offset',
              type: 'object',
              options: {
                collapsible: false,
              },
              fields: [
                {
                  name: 'name',
                  title: 'Name',
                  type: 'string',
                },
              ],
            },
          ],
          components: {
            input: WheelSizeSelector,
          },
        },
        {
          name: 'sameAsFront',
          title: 'Same as Front',
          type: 'boolean',
          initialValue: true,
        },
        {
          name: 'rear',
          title: 'Rear',
          type: 'object',
          options: {
            collapsible: false,
          },
          hidden: ({ parent }) => parent?.sameAsFront,
          fields: [
            {
              name: 'diameter',
              title: 'Diameter',
              type: 'object',
              options: {
                collapsible: false,
              },
              fields: [
                {
                  name: 'wheelDiameter',
                  title: 'Wheel Diameter',
                  description: 'The diameter of the wheel in inches',
                  type: 'number',
                },
                {
                  name: 'name',
                  title: 'Name (legacy)',
                  description: 'This field will be removed once migration is done',
                  readOnly: true,
                  type: 'string',
                },
              ],
            },
            {
              name: 'width',
              title: 'Width',
              type: 'reference',
              to: [{ type: 'width' }],
              validation: (Rule) => Rule.required().error('Width is required'),
            },
            {
              name: 'offset',
              title: 'Offset',
              type: 'object',
              options: {
                collapsible: false,
              },
              fields: [
                {
                  name: 'name',
                  title: 'Name',
                  type: 'string',
                },
              ],
            },
            {
              name: 'alternativeOffset',
              title: 'Alternative Offset',
              type: 'object',
              options: {
                collapsible: false,
              },
              fields: [
                {
                  name: 'name',
                  title: 'Name',
                  type: 'string',
                },
              ],
            },
          ],
          components: {
            input: WheelSizeSelector,
          },
        },
      ],
      group: 'specifications',
    }),

    // Tire Specifications
    defineField({
      name: 'tireDetails',
      title: 'Tires',
      type: 'object',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'spacers',
          title: 'Spacers',
          type: 'object',
          options: {
            columns: 2,
            collapsible: false,
          },
          fields: [
            {
              name: 'spacerFront',
              title: 'Spacer Front',
              type: 'reference',
              to: [{ type: 'spacer' }],
              components: {
                input: NumericReferenceInput,
              },
              options: {
                collapsible: false,
                disableNew: true,
              },
            },
            {
              name: 'spacerFrontReason',
              title: 'Spacer Front Reason',
              type: 'string',
              description: 'Optional reason for using this front spacer',
            },
            {
              name: 'spacerRear',
              title: 'Spacer Rear',
              type: 'reference',
              to: [{ type: 'spacer' }],
              components: {
                input: NumericReferenceInput,
              },
              options: {
                collapsible: false,
                disableNew: true,
              },
            },
            {
              name: 'spacerRearReason',
              title: 'Spacer Rear Reason',
              type: 'string',
              description: 'Optional reason for using this rear spacer',
            },
          ],
        },
        {
          name: 'cambers',
          title: 'Camber',
          type: 'object',
          options: {
            columns: 2,
            collapsible: false,
          },
          fields: [
            {
              name: 'camberFront',
              title: 'Camber Front',
              type: 'reference',
              description: 'Degree sign is added on render',
              to: [{ type: 'camber' }],
              components: {
                input: NumericReferenceInput,
              },
              options: {
                collapsible: false,
                disableNew: true,
              },
            },
            {
              name: 'camberFrontReason',
              title: 'Camber Front Reason',
              type: 'string',
              description: 'Optional reason for using this front camber',
            },
            {
              name: 'camberRear',
              title: 'Camber Rear',
              type: 'reference',
              description: 'Degree sign is added on render',
              to: [{ type: 'camber' }],
              components: {
                input: NumericReferenceInput,
              },
              options: {
                collapsible: false,
                disableNew: true,
              },
            },
            {
              name: 'camberRearReason',
              title: 'Camber Rear Reason',
              type: 'string',
              description: 'Optional reason for using this rear camber',
            },
          ],
        },
        {
          name: 'types',
          title: 'Tire Types',
          type: 'array',
          of: [
            {
              type: 'reference',
              to: [{ type: 'tireType' }],
              options: {
                disableNew: true,
              },
            },
          ],
        },
        {
          name: 'tireSize',
          title: 'Recommended Tire Size',
          type: 'fitmentListingTireSize',
          options: {
            collapsible: true,
            collapsed: false,
          },
        },
        {
          name: 'alternativeTireSizes',
          title: 'Alternative Tire Sizes',
          description:
            'Add additional tire size options that can be used with this fitment',
          type: 'array',
          of: [
            {
              type: 'object',
              name: 'alternativeTireSize',
              title: 'Alternative Tire Size',
              fields: [
                {
                  name: 'tireConfig',
                  title: 'Tire Configuration',
                  type: 'fitmentListingTireSize',
                },
                {
                  name: 'sameAsRecommendedSpacer',
                  title: 'Same as Recommended Spacer',
                  type: 'boolean',
                  initialValue: true,
                  description:
                    'Use the same spacer configuration as the recommended tire size',
                },
                {
                  name: 'spacers',
                  title: 'Spacers',
                  type: 'object',
                  hidden: ({ parent }) => parent?.sameAsRecommendedSpacer,
                  options: {
                    columns: 2,
                    collapsible: false,
                  },
                  fields: [
                    {
                      name: 'spacerFront',
                      title: 'Spacer Front',
                      type: 'reference',
                      to: [{ type: 'spacer' }],
                      // TODO: Uncomment after NumericReferenceInput migration
                      // components: {
                      //   input: NumericReferenceInput,
                      // },
                      options: {
                        collapsible: false,
                        disableNew: true,
                      },
                    },
                    {
                      name: 'spacerFrontReason',
                      title: 'Spacer Front Reason',
                      type: 'string',
                      description: 'Optional reason for using this front spacer',
                    },
                    {
                      name: 'spacerRear',
                      title: 'Spacer Rear',
                      type: 'reference',
                      to: [{ type: 'spacer' }],
                      // TODO: Uncomment after NumericReferenceInput migration
                      // components: {
                      //   input: NumericReferenceInput,
                      // },
                      options: {
                        collapsible: false,
                        disableNew: true,
                      },
                    },
                    {
                      name: 'spacerRearReason',
                      title: 'Spacer Rear Reason',
                      type: 'string',
                      description: 'Optional reason for using this rear spacer',
                    },
                  ],
                },
                {
                  name: 'sameAsRecommendedCamber',
                  title: 'Same as Recommended Camber',
                  type: 'boolean',
                  initialValue: true,
                  description:
                    'Use the same camber configuration as the recommended tire size',
                },
                {
                  name: 'cambers',
                  title: 'Camber',
                  type: 'object',
                  hidden: ({ parent }) => parent?.sameAsRecommendedCamber,
                  options: {
                    columns: 2,
                    collapsible: false,
                  },
                  fields: [
                    {
                      name: 'camberFront',
                      title: 'Camber Front',
                      type: 'reference',
                      description: 'Degree sign is added on render',
                      to: [{ type: 'camber' }],
                      // TODO: Uncomment after NumericReferenceInput migration
                      // components: {
                      //   input: NumericReferenceInput,
                      // },
                      options: {
                        collapsible: false,
                        disableNew: true,
                      },
                    },
                    {
                      name: 'camberFrontReason',
                      title: 'Camber Front Reason',
                      type: 'string',
                      description: 'Optional reason for using this front camber',
                    },
                    {
                      name: 'camberRear',
                      title: 'Camber Rear',
                      type: 'reference',
                      description: 'Degree sign is added on render',
                      to: [{ type: 'camber' }],
                      // TODO: Uncomment after NumericReferenceInput migration
                      // components: {
                      //   input: NumericReferenceInput,
                      // },
                      options: {
                        collapsible: false,
                        disableNew: true,
                      },
                    },
                    {
                      name: 'camberRearReason',
                      title: 'Camber Rear Reason',
                      type: 'string',
                      description: 'Optional reason for using this rear camber',
                    },
                  ],
                },
              ],
              preview: {
                select: {
                  format: 'tireConfig.front.defaultTireFormat',
                  treadWidthMetric: 'tireConfig.front.treadWidthMetric',
                  tireDiameterMetric: 'tireConfig.front.tireDiameterMetric',
                  tireDiameterImperial: 'tireConfig.front.tireDiameterImperial',
                  treadWidthImperial: 'tireConfig.front.treadWidthImperial',
                  aspectRatio: 'tireConfig.front.aspectRatio',
                  wheelDiameterRef: 'tireConfig.front.diameter.wheelDiameter',
                  rearFormat: 'tireConfig.rear.size.defaultTireFormat',
                  rearTreadWidthMetric: 'tireConfig.rear.size.treadWidthMetric',
                  rearTireDiameterMetric: 'tireConfig.rear.size.tireDiameterMetric',
                  rearTireDiameterImperial:
                    'tireConfig.rear.size.tireDiameterImperial',
                  rearTreadWidthImperial: 'tireConfig.rear.size.treadWidthImperial',
                  rearAspectRatio: 'tireConfig.rear.size.aspectRatio',
                  rearWheelDiameterRef: 'tireConfig.rear.size.diameter.wheelDiameter',
                },
                prepare({
                  format,
                  treadWidthMetric,
                  tireDiameterMetric,
                  tireDiameterImperial,
                  treadWidthImperial,
                  aspectRatio,
                  wheelDiameterRef,
                  rearFormat,
                  rearTreadWidthMetric,
                  rearTireDiameterMetric,
                  rearTireDiameterImperial,
                  rearTreadWidthImperial,
                  rearAspectRatio,
                  rearWheelDiameterRef,
                }) {
                  let title = ''
                  // Generate front tire format
                  let frontTireFormat = ''
                  switch (format) {
                    case 'standard':
                      frontTireFormat = `${treadWidthMetric || ''}/${aspectRatio || ''}-${wheelDiameterRef || ''}`
                      break
                    case 'slick':
                      frontTireFormat = `${treadWidthMetric || ''}/${tireDiameterMetric || ''}-${wheelDiameterRef || ''}`
                      break
                    case 'offroad':
                      frontTireFormat = `${tireDiameterImperial || ''}x${treadWidthImperial || ''}R${wheelDiameterRef || ''}`
                      break
                    default:
                      frontTireFormat = ''
                  }

                  // Generate rear tire format
                  let rearTireFormat = ''
                  if (rearFormat) {
                    // Different format for rear
                    switch (rearFormat) {
                      case 'standard':
                        rearTireFormat = `${rearTreadWidthMetric || ''}/${rearAspectRatio || ''}-${rearWheelDiameterRef || ''}`
                        break
                      case 'slick':
                        rearTireFormat = `${rearTreadWidthMetric || ''}/${rearTireDiameterMetric || ''}-${rearWheelDiameterRef || ''}`
                        break
                      case 'offroad':
                        rearTireFormat = `${rearTireDiameterImperial || ''}x${rearTreadWidthImperial || ''}R${rearWheelDiameterRef || ''}`
                        break
                      default:
                        rearTireFormat = ''
                    }
                  } else {
                    // Use front format for rear if rearFormat is empty
                    rearTireFormat = frontTireFormat
                  }

                  // Prefix front with F: and rear with R:
                  title = `Front: ${frontTireFormat} / Rear: ${rearTireFormat}`

                  // Format the subtitle to show both front and rear formats
                  const frontFormatName = format
                    ? `${format.charAt(0).toUpperCase() + format.slice(1)}`
                    : ''
                  const rearFormatName = rearFormat
                    ? `${rearFormat.charAt(0).toUpperCase() + rearFormat.slice(1)}`
                    : frontFormatName

                  const subtitle = `Front: ${frontFormatName} / Rear: ${rearFormatName}`

                  return {
                    title,
                    subtitle,
                  }
                },
              },
            },
          ],
          options: {
            collapsible: true,
            collapsed: false,
          },
        },
      ],
      group: 'specifications',
    }),

    // Content
    defineField({
      name: 'content',
      title: 'Content',
      type: 'object',
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'flag',
          title: 'Flag',
          description: 'Displays listing with a styled border and custom flag.',
          type: 'string',
          options: {
            list: [
              { title: 'No flag', value: '' },
              { title: 'Most popular', value: 'most-popular' },
              { title: 'Staff pick', value: 'staff-pick' },
              { title: 'Recommended', value: 'recommended' },
            ],
            layout: 'radio',
          },
          initialValue: '',
        },
        {
          name: 'tags',
          title: 'Fitment tags',
          type: 'array',
          of: [
            {
              type: 'reference',
              to: [{ type: 'fitmentTag' }],
              options: {
                disableNew: true,
                filter: () => {
                  return {
                    filter:
                      '!(_id in ["forgedTag", "flowFormedTag", "squareTag", "rotateableTag", "staggeredTag", "doubleStaggeredTag", "reverseStaggeredTag", "dualOffsetTag"])',
                  }
                },
              },
            },
          ],
        },
        {
          name: 'extraFitmentNotes',
          title: 'Extra fitment notes',
          description:
            'Use this field if you want to add notes that are specific to this listing.',
          type: 'array',
          of: [
            {
              type: 'object',
              name: 'extraNote',
              title: 'Note',
              fields: [
                {
                  name: 'content',
                  title: 'Content',
                  type: 'array',
                  of: [
                    {
                      type: 'block',
                      styles: [{ title: 'Normal', value: 'normal' }],
                      marks: {
                        decorators: [
                          { title: 'Strong', value: 'strong' },
                          { title: 'Emphasis', value: 'em' },
                        ],
                        annotations: [
                          {
                            name: 'link',
                            type: 'object',
                            fields: [
                              {
                                name: 'href',
                                type: 'url',
                                title: 'URL',
                              },
                            ],
                          },
                        ],
                      },
                    },
                  ],
                },
                {
                  name: 'penaltyScore',
                  title: 'Penalty Score',
                  type: 'number',
                  description:
                    'Points deducted from "Ease of fit" score when this note is added. Higher penalties indicate more difficult fitments. Maximum penalty is 10 points.',
                  validation: (Rule) => Rule.min(0).max(10).precision(1),
                  initialValue: 0,
                },
              ],
              preview: {
                select: {
                  content: 'content',
                  penaltyScore: 'penaltyScore',
                },
                prepare({ content, penaltyScore }) {
                  // Extract text from the first block
                  let text = 'Empty note'
                  if (content && content.length > 0) {
                    const firstBlock = content[0]
                    if (firstBlock.children && firstBlock.children.length > 0) {
                      text = firstBlock.children
                        .filter((child: any) => child.text)
                        .map((child: any) => child.text)
                        .join(' ')
                        .substring(0, 50)
                      if (text.length === 50) {
                        text += '...'
                      }
                    }
                  }

                  const subtitle =
                    penaltyScore > 0
                      ? `Ease of fit score penalty: -${penaltyScore}`
                      : 'No penalty'

                  return {
                    title: text,
                    subtitle: subtitle,
                  }
                },
              },
            },
          ],
        },
        {
          name: 'overview',
          title: 'Overview',
          type: 'array',
          of: [{ type: 'block' }],
        },
        {
          name: 'apexAnalysis',
          title: 'Apex Analysis',
          type: 'array',
          of: [{ type: 'block' }],
        },
      ],
      group: 'specifications',
    }),

    // AI Content Tab
    defineField({
      name: 'userContext',
      title: 'Additional Context for AI',
      description:
        'Optional notes to provide additional context for AI generation (e.g., special considerations, unique aspects of this fitment). This context will be included in the AI prompt.',
      type: 'text',
      rows: 3,
      group: 'aiContent',
    }),
    defineField({
      name: 'aiRegenerate',
      title: 'Regenerate AI Content',
      type: 'object',
      group: 'aiContent',
      components: {
        input: ListingAIRegenerateButton,
      },
      fields: [
        {
          name: 'placeholder',
          type: 'string',
        },
      ],
    }),
    defineField({
      name: 'contentOverrides',
      title: 'AI Content & Overrides',
      type: 'object',
      group: 'aiContent',
      description:
        'View AI-generated content and add manual overrides. Overrides take priority on the frontend.',
      components: {
        input: AIContentWithOverrideInput,
      },
      fields: [
        {
          name: 'overview',
          title: 'Overview Override',
          type: 'text',
          rows: 4,
        },
        {
          name: 'performanceAnalysis',
          title: 'Performance Analysis Override',
          type: 'text',
          rows: 4,
        },
        {
          name: 'easeOfFitment',
          title: 'Ease of Fitment Override',
          type: 'text',
          rows: 4,
        },
        {
          name: 'tireAvailability',
          title: 'Tire Availability Override',
          type: 'text',
          rows: 4,
        },
      ],
    }),
    defineField({
      name: 'aiGeneratedContent',
      title: 'AI Generated Descriptions',
      type: 'object',
      group: 'aiContent',
      hidden: true,
      fields: [
        {
          name: 'overview',
          title: 'Overview',
          type: 'object',
          options: {
            collapsible: false,
          },
          fields: [
            {
              name: 'content',
              title: 'Generated Content',
              type: 'text',
              rows: 4,
              readOnly: true,
            },
            {
              name: 'lastGenerated',
              title: 'Last Generated',
              type: 'datetime',
              readOnly: true,
            },
            {
              name: 'rulesVersion',
              title: 'Rules Version',
              type: 'string',
              readOnly: true,
            },
            {
              name: 'contextHash',
              title: 'Context Hash',
              type: 'string',
              hidden: true,
            },
          ],
        },
        {
          name: 'performanceAnalysis',
          title: 'Performance Analysis',
          type: 'object',
          options: {
            collapsible: false,
          },
          fields: [
            {
              name: 'content',
              title: 'Generated Content',
              type: 'text',
              rows: 4,
              readOnly: true,
            },
            {
              name: 'lastGenerated',
              title: 'Last Generated',
              type: 'datetime',
              readOnly: true,
            },
            {
              name: 'rulesVersion',
              title: 'Rules Version',
              type: 'string',
              readOnly: true,
            },
            {
              name: 'contextHash',
              title: 'Context Hash',
              type: 'string',
              hidden: true,
            },
          ],
        },
        {
          name: 'easeOfFitment',
          title: 'Ease of Fitment',
          type: 'object',
          options: {
            collapsible: false,
          },
          fields: [
            {
              name: 'content',
              title: 'Generated Content',
              type: 'text',
              rows: 4,
              readOnly: true,
            },
            {
              name: 'lastGenerated',
              title: 'Last Generated',
              type: 'datetime',
              readOnly: true,
            },
            {
              name: 'rulesVersion',
              title: 'Rules Version',
              type: 'string',
              readOnly: true,
            },
            {
              name: 'contextHash',
              title: 'Context Hash',
              type: 'string',
              hidden: true,
            },
          ],
        },
        {
          name: 'tireAvailability',
          title: 'Tire Availability',
          type: 'object',
          options: {
            collapsible: false,
          },
          fields: [
            {
              name: 'content',
              title: 'Generated Content',
              type: 'text',
              rows: 4,
              readOnly: true,
            },
            {
              name: 'lastGenerated',
              title: 'Last Generated',
              type: 'datetime',
              readOnly: true,
            },
            {
              name: 'rulesVersion',
              title: 'Rules Version',
              type: 'string',
              readOnly: true,
            },
            {
              name: 'contextHash',
              title: 'Context Hash',
              type: 'string',
              hidden: true,
            },
          ],
        },
      ],
    }),

    // Scoring Group
    defineField({
      name: 'scoringValues',
      title: 'Scoring Values',
      type: 'array',
      group: 'scoring',
      components: {
        input: ScoringInput,
      },
      of: [
        {
          type: 'object',
          fields: [
            {
              name: 'scoreCriteria',
              type: 'reference',
              to: [{ type: 'scoringCriteria' }],
            },
            {
              name: 'score',
              type: 'number',
            },
          ],
        },
      ],
    }),

    // Performance Scores Group
    defineField({
      name: 'performanceScores',
      title: 'Performance Scores',
      type: 'object',
      group: 'performanceScore',
      description: 'Auto-calculated performance scores. Do not edit manually.',
      components: {
        input: PerformanceScoreDisplay,
      },
      options: {
        collapsible: true,
        collapsed: false,
      },
      fields: [
        {
          name: 'wheelWidthScore',
          title: 'Wheel Width Score',
          type: 'number',
          readOnly: true,
        },
        {
          name: 'tireWidthScore',
          title: 'Tire Width Score',
          type: 'number',
          readOnly: true,
        },
        {
          name: 'tireCategoryScore',
          title: 'Tire Category Score',
          type: 'number',
          readOnly: true,
        },
        {
          name: 'diameterScore',
          title: 'Diameter Score',
          type: 'number',
          readOnly: true,
        },
        {
          name: 'rawScore',
          title: 'Raw Score (weighted average)',
          type: 'number',
          readOnly: true,
        },
        {
          name: 'normalizedScore',
          title: 'Normalized Score (0-10)',
          type: 'number',
          readOnly: true,
        },
        {
          name: 'lastCalculated',
          title: 'Last Calculated',
          type: 'datetime',
          readOnly: true,
        },
        {
          name: 'contextHash',
          title: 'Context Hash',
          type: 'string',
          hidden: true,
        },
      ],
    }),

    // Hidden
    defineField({
      name: 'order',
      title: 'Order',
      type: 'string',
      hidden: true,
    }),
  ],
  preview: {
    select: {
      frontDiameter: 'wheelSize.front.diameter.wheelDiameter',
      frontWidth: 'wheelSize.front.width.name',
      frontOffset: 'wheelSize.front.offset.name',
      rearSameAsFront: 'wheelSize.sameAsFront',
      rearDiameter: 'wheelSize.rear.diameter.wheelDiameter',
      rearWidth: 'wheelSize.rear.width.name',
      rearOffset: 'wheelSize.rear.offset.name',
      order: 'order',
    },
    prepare({
      order,
      frontDiameter,
      frontWidth,
      frontOffset,
      rearSameAsFront,
      rearWidth,
      rearDiameter,
      rearOffset,
    }) {
      const front = `${frontDiameter}x${frontWidth} ${frontOffset}`

      const rear = rearSameAsFront
        ? front
        : `${rearDiameter}x${rearWidth} ${rearOffset}`

      return {
        title: `(${order || '?'}) Front: ${front} / Rear: ${rear}`,
      }
    },
  },
})
